import { useStore } from '@nanostores/react';
import {
  announceAttemptTransition,
  announceAttemptHydraTransition,
  childAppLoadedStore,
  setActiveRoute,
  setNavCollapsed,
  subscribeToTransitionConfirmation,
  subscribeToHydraTransitionConfirmation,
} from '@customerio/hydra-nav-state';
import { createContext, use, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setAppContainerFadedOut, setAppContainerOutletRendered } from '@/stores/root';
import { getAppBundleFromUrl } from '@/utils/navigation';
import type { AppBundle } from '@/utils/app-bundles';
import { useActiveBundle } from '../../context/active-bundle';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

export interface TargetTransitionOptions {
  shouldFadeOut?: boolean;
  shouldUnrenderOutlet?: boolean;
  shouldReplaceHistoryEntry?: boolean;
  targetApp?: AppBundle;
  /** Passed through to react-router's navigate() as location state. */
  state?: Record<string, unknown>;
}

export interface TargetTransition {
  url: string;
  options?: TargetTransitionOptions;
}

async function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type SafeNavigateFunction = {
  (url: string, options?: TargetTransitionOptions): void;
  /**
   * Navigate back in history. Only supports -1. Silently does nothing if
   * there is no same-origin back entry (e.g. user landed on the page
   * directly). Bypasses the transition machinery because the browser
   * restores the previous page state directly.
   */
  (delta: -1): void;
};

const SafeNavigateContext = createContext<SafeNavigateFunction | null>(null);

function useSafeNavigateInternal() {
  const navigate = useNavigate();
  const childAppLoaded = useStore(childAppLoadedStore);
  const activeApp = useActiveBundle();

  // Use refs and update them with effects so that we can reference the current
  // values in the below callbacks without having to add them to the dependency
  // arrays.
  const childAppLoadedRef = useRef(childAppLoaded);
  useEffect(() => {
    childAppLoadedRef.current = childAppLoaded;
  }, [childAppLoaded]);
  const activeAppRef = useRef(activeApp);
  useEffect(() => {
    activeAppRef.current = activeApp;
  }, [activeApp]);

  const unsubscribeFromTransitionConfirmation = useRef<(() => void) | null>(null);

  const nextTransition = useRef<TargetTransition | null>(null);

  const performNavigate = useCallback(
    async (transition: TargetTransition | null) => {
      unsubscribeFromTransitionConfirmation.current?.();

      if (transition != null) {
        setNavCollapsed(false);
        if (transition.options?.shouldFadeOut ?? false) {
          setAppContainerFadedOut(true);
          // Wait for animation to finish
          await timeout(250);
        }
        if (transition.options?.shouldUnrenderOutlet ?? false) {
          // A timeout of 0 makes sure the outlet is destroyed before continuing
          // with the transition
          await timeout(0);
          setAppContainerOutletRendered(false);
        }

        // Only call "navigate" if:
        // - we're unrendering the main outlet - which usually means we're
        //   navigating to a different app.
        //   If we're not unrendering the outlet, the child app is responsible
        //   for all "navigate" events and has control over the browser's history
        //   (so that we don't create duplicate history entries).
        // or
        // - there is no active app - which means there is no embedded app and
        //   we're navigating between Hydra UI routes
        if ((transition.options?.shouldUnrenderOutlet ?? false) || activeAppRef.current == null) {
          navigate(transition.url, {
            replace: transition.options?.shouldReplaceHistoryEntry ?? false,
            state: transition.options?.state,
          });
        }

        setActiveRoute(transition.url);

        if (transition.options?.shouldFadeOut ?? false) {
          setAppContainerFadedOut(false);
        }
        if (transition.options?.shouldUnrenderOutlet ?? false) {
          setAppContainerOutletRendered(true);
        }
      }
    },
    [navigate],
  );

  // If there is a stored transition once the child app has loaded, perform the
  // transition.
  // A stored transition can occur when a user clicks a link while the child app
  // is still loading.
  useEffect(() => {
    if (nextTransition.current != null && childAppLoaded === true) {
      const transition = nextTransition.current;
      nextTransition.current = null;

      performNavigate(transition);
    }

    if (childAppLoaded === true) {
      nextTransition.current = null;
    }
  }, [childAppLoaded, performNavigate]);

  // Clean up when components unmount
  useMountEffect(() => {
    return () => {
      nextTransition.current = null;
      unsubscribeFromTransitionConfirmation.current?.();
    };
  });

  const safeNavigate: SafeNavigateFunction = useCallback(
    (urlOrDelta: string | number, options?: TargetTransitionOptions) => {
      // Support -1 to go back, passing through to navigate directly.
      if (typeof urlOrDelta === 'number') {
        if (urlOrDelta === -1) {
          navigate(-1);
        }
        return;
      }

      const url = urlOrDelta;

      // Reset navigation states on each click - there can only be one active
      // navigation attempt at a time.
      nextTransition.current = null;

      const isDifferentApp =
        options?.targetApp != null
          ? options.targetApp !== activeAppRef.current
          : getAppBundleFromUrl(url) !== activeAppRef.current;
      const shouldUnrenderOutlet = (options?.shouldUnrenderOutlet ?? false) || url === '/logout' || isDifferentApp;

      const transition = {
        url,
        options: {
          shouldFadeOut: options?.shouldFadeOut ?? false,
          shouldUnrenderOutlet,
          shouldReplaceHistoryEntry: options?.shouldReplaceHistoryEntry ?? false,
          state: options?.state,
        },
      };
      if (activeAppRef.current != null && childAppLoadedRef.current !== true) {
        nextTransition.current = transition;
      } else if (activeAppRef.current == null) {
        // Navigate for hydra-only pages
        unsubscribeFromTransitionConfirmation.current?.();
        unsubscribeFromTransitionConfirmation.current = subscribeToHydraTransitionConfirmation(() =>
          performNavigate(transition),
        );

        announceAttemptHydraTransition();
      } else {
        unsubscribeFromTransitionConfirmation.current?.();
        unsubscribeFromTransitionConfirmation.current = subscribeToTransitionConfirmation(() =>
          performNavigate(transition),
        );
        announceAttemptTransition();
      }
    },
    [navigate, performNavigate],
  );

  return safeNavigate;
}

/**
 * Returns true if there is a previous in-app history entry to go back to.
 * Uses React Router's location key — the initial entry has key "default",
 * so any other key means the user navigated here from within the app.
 */
export function useCanGoBack() {
  const location = useLocation();
  return location.key !== 'default';
}

export function useSafeNavigate() {
  const safeNavigate = use(SafeNavigateContext);

  if (safeNavigate == null) {
    throw new Error('useSafeNavigate must be used within a SafeNavigateProvider');
  }

  return safeNavigate;
}

export function SafeNavigateProvider({ children }: { children: React.ReactNode }) {
  const safeNavigate = useSafeNavigateInternal();

  return <SafeNavigateContext.Provider value={safeNavigate}>{children}</SafeNavigateContext.Provider>;
}

interface SafeNavigateProps {
  to: string;
  options?: TargetTransitionOptions;
}

/**
 * Declarative component that triggers a safe navigation on mount.
 * Use this instead of React Router's `<Navigate>` to go through the
 * safe navigation machinery (transition announcements, fade-out, etc.).
 *
 * Renders nothing.
 */
export function SafeNavigate({ to, options }: SafeNavigateProps) {
  const safeNavigate = useSafeNavigate();
  const hasNavigated = useRef(false);
  const { shouldFadeOut, shouldUnrenderOutlet, shouldReplaceHistoryEntry, targetApp, state } = options ?? {};

  useEffect(() => {
    if (hasNavigated.current === true) {
      return;
    }
    hasNavigated.current = true;
    safeNavigate(to, { shouldFadeOut, shouldUnrenderOutlet, shouldReplaceHistoryEntry, targetApp, state });
  }, [safeNavigate, to, shouldFadeOut, shouldUnrenderOutlet, shouldReplaceHistoryEntry, targetApp, state]);

  return null;
}
